'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { AnimatedButton } from '@/components/ui/animated-button';
import { useAnnouncer } from '@/components/ui/announcer';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';

interface ArchiveWalletProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  walletLabel: string;
  onArchive: () => Promise<void>;
}

export function ArchiveWallet({
  open,
  onOpenChange,
  walletLabel,
  onArchive,
}: ArchiveWalletProps): React.ReactNode {
  const t = useTranslations('wallet.archive');
  const { announce } = useAnnouncer();
  const { enabled: multiWalletsEnabled, canStartOperation } = useMultiWalletFeature();
  const [isArchiving, setIsArchiving] = useState<boolean>(false);

  async function handleArchive(): Promise<void> {
    if (!canStartOperation('archive', 'wallet_management')) {
      onOpenChange(false);
      return;
    }
    setIsArchiving(true);
    await runWithCleanup(
      async () => {
        try {
          await onArchive();
          announce(`Wallet archived: ${walletLabel}`);
          showToast({
            id: TOAST_IDS.WALLET_ARCHIVED,
            message: t('success'),
            variant: 'success',
          });
          onOpenChange(false);
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : String(err);
          logger.error('Wallet archive failed', { error: message.slice(0, 200) });
          onOpenChange(false);
          const lowerMsg = message.toLowerCase();
          if (lowerMsg.includes('pending gift') || lowerMsg.includes('gift_deposit')) {
            showToast({
              id: TOAST_IDS.WALLET_ARCHIVED,
              message: t('error_has_pending_gifts'),
              variant: 'error',
              duration: 6000,
            });
          } else if (lowerMsg.includes('position') || lowerMsg.includes('open')) {
            showToast({
              id: TOAST_IDS.WALLET_ARCHIVED,
              message: t('error_has_positions'),
              variant: 'error',
              duration: 6000,
            });
          } else {
            showToast({
              id: TOAST_IDS.WALLET_ARCHIVED,
              message: t('error'),
              variant: 'error',
            });
          }
        }
      },
      () => setIsArchiving(false),
    );
  }

  return (
    <SilkSheet
      open={open && multiWalletsEnabled}
      onOpenChange={onOpenChange}
      variant="detached"
      size="sm"
      sheetClassName="sm:aspect-[4/3]"
      title={t('title')}
      description={t('body')}
      footer={
        <ModalFooter>
          <AnimatedButton
            variant="secondary"
            onClick={(): void => {
              onOpenChange(false);
            }}
            disabled={isArchiving}
          >
            {t('cancel')}
          </AnimatedButton>
          <AnimatedButton
            variant="destructive"
            onClick={handleArchive}
            disabled={isArchiving}
            loading={isArchiving}
          >
            {t('confirm')}
          </AnimatedButton>
        </ModalFooter>
      }
    ></SilkSheet>
  );
}
